import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeamento de rotas para nomes amigáveis
const PAGE_NAMES = {
  '/meus-livros': 'Meus Livros',
  '/novo-livro': 'Criar Novo Livro',
  '/dashboard': 'Progresso Criativo',
  '/cafeteria': 'Cafeteria Best Seller',
  '/bau-intelectual': 'Baú Intelectual',
  '/arquivo-intelectual': 'Arquivo Intelectual',
  '/emulador-acelerador': 'Emulador Acelerador',
  '/empresario-literario': 'Empresário Literário',
  '/perfil-do-autor': 'Perfil do Autor',
  '/configuracoes': 'Configurações',
  '/seguranca': 'Segurança',
  '/entrada': 'Página Inicial',
  '/editor': 'Editor de Capítulo',
  '/livro': 'Estrutura do Livro',
};

const getPageName = (pathname) => {
  // Check exact match first
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  
  // Check partial matches
  for (const [path, name] of Object.entries(PAGE_NAMES)) {
    if (pathname.startsWith(path)) return name;
  }
  
  return 'Author\'s Key';
};

export default function ConciergeChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  // Clear conversation when closing chat
  const handleClose = async () => {
    setIsOpen(false);
    // Clear conversation on close
    try {
      await axios.delete(`${API_URL}/api/concierge/history`, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error clearing history:', error);
    }
    // Reset local state
    setMessages([]);
    setInput('');
    setShowEscalate(false);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const pageName = getPageName(location.pathname);
      setMessages([{
        role: 'assistant',
        content: `Olá! Sou Speer, seu guia no Author's Key. 🔑\n\nVejo que você está em "${pageName}". Como posso ajudar?`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, location.pathname]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowEscalate(false);

    try {
      const response = await axios.post(`${API_URL}/api/concierge/chat`, {
        message: userMessage.content,
        page_context: getPageName(location.pathname)
      }, {
        withCredentials: true
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Show escalate option if can't help
      if (!response.data.can_help) {
        setShowEscalate(true);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, tive um problema técnico. Tente novamente ou entre em contato com o suporte.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
      setShowEscalate(true);
    } finally {
      setIsLoading(false);
    }
  };

  const escalateToSupport = async (urgency) => {
    try {
      const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
      
      await axios.post(`${API_URL}/api/concierge/escalate`, {
        message: lastUserMessage?.content || 'Solicitação de suporte',
        urgency,
        user_context: `Página: ${getPageName(location.pathname)}`
      }, {
        withCredentials: true
      });

      toast.success('Sua solicitação foi enviada para nossa equipe!');
      setShowEscalate(false);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Pronto! Encaminhei sua dúvida para nossa equipe com urgência "${urgency === 'high' ? 'alta' : urgency === 'medium' ? 'média' : 'normal'}". Você receberá uma resposta em breve. 📧`,
        timestamp: new Date().toISOString()
      }]);

    } catch (error) {
      console.error('Error escalating:', error);
      toast.error('Erro ao enviar. Tente novamente.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button - Just the key image */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 z-[9998] transition-all duration-300 hover:scale-110 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0
        }}
        data-testid="concierge-button"
        aria-label="Abrir assistente Speer"
        title="Speer - Seu guia no Author's Key"
      >
        <img 
          src="/icons/chave-simpatica.png" 
          alt="Speer"
          className="w-[120px] h-[120px] object-contain drop-shadow-lg"
          style={{ background: 'transparent' }}
          onError={(e) => {
            console.error('Error loading concierge icon');
            e.target.style.display = 'none';
          }}
        />
      </button>

      {/* Chat Window - Smaller size */}
      <div
        className={`fixed bottom-6 right-6 z-[9998] w-[320px] h-[420px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{
          transformOrigin: 'bottom right',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}
        data-testid="concierge-chat"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 flex items-center gap-2">
          <img 
            src="/icons/chave-simpatica.png" 
            alt="Speer"
            className="w-8 h-8 object-contain"
          />
          <div className="flex-1">
            <h3 className="text-white font-semibold text-sm">Speer</h3>
            <p className="text-white/80 text-[10px]">Seu guia no Author's Key</p>
          </div>
          <button
            onClick={handleClose}
            className="text-white/70 hover:text-white p-1 transition-colors"
            title="Encerrar conversa"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/50">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                  msg.role === 'user'
                    ? 'bg-amber-500 text-white rounded-br-md'
                    : msg.error
                      ? 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20 rounded-bl-md'
                      : 'bg-muted text-foreground rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <Loader2 size={18} className="animate-spin text-amber-500" />
              </div>
            </div>
          )}

          {/* Escalate Options */}
          {showEscalate && !isLoading && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={16} className="text-amber-600" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Encaminhar para suporte?
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => escalateToSupport('low')}
                  className="flex-1 text-xs bg-stone-200 dark:bg-stone-700 hover:bg-stone-300 dark:hover:bg-stone-600 rounded-lg py-1.5 transition-colors"
                >
                  Normal
                </button>
                <button
                  onClick={() => escalateToSupport('medium')}
                  className="flex-1 text-xs bg-amber-200 dark:bg-amber-800 hover:bg-amber-300 dark:hover:bg-amber-700 text-amber-800 dark:text-amber-200 rounded-lg py-1.5 transition-colors"
                >
                  Importante
                </button>
                <button
                  onClick={() => escalateToSupport('high')}
                  className="flex-1 text-xs bg-red-200 dark:bg-red-800 hover:bg-red-300 dark:hover:bg-red-700 text-red-800 dark:text-red-200 rounded-lg py-1.5 transition-colors"
                >
                  Urgente
                </button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 border-t border-border bg-card">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua dúvida..."
              className="flex-1 bg-muted border-0 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              disabled={isLoading}
              data-testid="concierge-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              data-testid="concierge-send"
            >
              <Send size={18} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
